import { useEffect } from "react";
import { useAppDispatch, useAppSelector } from "lib/redux/hooks";
import { updateEncounterIdForRegister } from "../ducks/appointmentsSlice";
import { isNUP } from "lib/util/ValidatorUtil/isNupValidator/isNupValidator";
import { selectKioskQuestionnaire } from "../ducks/selectors";
import { fetchKioskQuestionnaire } from "../ducks/thunks";

/**
 * Custom hook to fetch kiosk questionnaire based on various parameters.
 * It dispatches an action to fetch the questionnaire and updates the encounter ID if necessary.
 *
 * @param {Object} params - The parameters for fetching the kiosk questionnaire.
 * @param {boolean} params.pageFlag - Flag indicating if the page is active.
 * @param {boolean} params.shouldGetKioskQuestionnaire - Flag indicating if the questionnaire should be fetched.
 * @param {string | null} params.memberIdentifier - Member identifier for fetching the questionnaire.
 * @param {string | null} params.regInstitutionCode - Registration institution code.
 * @param {string | null} params.institutionCode - Institution code.
 * @param {string | null} params.targetSystem - Target system for fetching the questionnaire.
 * @param {string | null} params.clinicCode - Clinic code.
 * @param {string | null} params.visitType - Visit type for the appointment.
 * @param {string | null} params.appointmentId - Appointment ID for updating encounter ID.
 * @param {string | null} params.logoCode - Logo code for validation.
 * @param {boolean} params.isFromMobile - Flag indicating if the request is from a mobile device.
 */
const useGetKioskQuestionnaire = ({
  pageFlag,
  shouldGetKioskQuestionnaire,
  memberIdentifier,
  regInstitutionCode,
  institutionCode,
  targetSystem,
  clinicCode,
  visitType,
  appointmentId,
  logoCode,
  isFromMobile,
}: {
  pageFlag: boolean;
  shouldGetKioskQuestionnaire: boolean;
  memberIdentifier: string | null;
  regInstitutionCode: string | null;
  institutionCode: string | null;
  targetSystem: string | null;
  clinicCode: string | null;
  visitType: string | null;
  appointmentId: string | null;
  logoCode: string | null;
  isFromMobile: boolean;
}) => {
  const dispatch = useAppDispatch();

  // Redux State
  const { questions, encounterId } = useAppSelector(selectKioskQuestionnaire);

  useEffect(() => {
    if (shouldGetKioskQuestionnaire) {
      dispatch(
        fetchKioskQuestionnaire({
          memberIdentifier,
          regInstitutionCode,
          institutionCode,
          targetSystem,
          clinicCode,
          visitType,
          isFromMobile,
        }),
      );

      if (isNUP(institutionCode, logoCode)) {
        dispatch(
          updateEncounterIdForRegister({
            encounterId: encounterId,
            appointmentId: appointmentId ?? "",
          }),
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    memberIdentifier,
    regInstitutionCode,
    institutionCode,
    targetSystem,
    clinicCode,
    visitType,
    pageFlag,
    shouldGetKioskQuestionnaire,
    appointmentId,
    logoCode,
    isFromMobile,
    encounterId,
    questions.length,
  ]);
};

export { useGetKioskQuestionnaire };
